import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EAppointmentType, EServiceGroup, ROUTES, WORDPRESS_URL } from "@constants";
import { useLinkProspect } from "@hooks/useLinkProspect";
import { clearStorage, getCart, IStorage, loadStorage } from "@utils/storage";
import "./css/confirmationPage.scss";
import { useHistory } from "react-router-dom";
import { Grid, SvgIcon, Typography } from "@mui/material";
import { keycloakUtils } from "itsf-ui-common";
import { selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { FlowCard } from "@common/FlowCard/FlowCard";
import { ConfirmationMobile } from "@features/confirmation/ConfirmationMobile";
import { ConfirmationPreOrder } from "@features/confirmation/ConfirmationPreOrder";
import { ConfirmationNoInstall } from "@features/confirmation/ConfirmationNoInstall";
import { ConfirmationFullInstall } from "@features/confirmation/ConfirmationFullInstall";
import { ICartOffer } from "@features/cart/ICart";
import { useStyle } from "./style";
import confirmationImage from "@static/img/broadband/confirmation.png";
import CheckIcon from "@static/icons/Check.svg";
import { useDeviceType } from "@hooks/useDeviceType";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { sendPurchaseEvent } from "@features/dataLayer/purchase";
import { Helmet } from "react-helmet";

export const ConfirmationPage = () => {
    const { linkProspect } = useLinkProspect();
    const [savedStorage, setSavedStorage] = useState<IStorage>();
    const { t } = useTranslation("confirmation");
    const { push } = useHistory();
    const { isTabletOrDesktop } = useDeviceType();
    const { classes } = useStyle();

    useEffect(() => {
        // The only way to have a cart is to validate payment
        const cart = getCart();
        if (cart?.offers.length) {
            const storage: IStorage | undefined = loadStorage();
            setSavedStorage(storage);
            if (storage) {
                sendPurchaseEvent(storage);
            }
            clearStorage();

            if (keycloakUtils.isAuthenticated()) {
                linkProspect();
            }
        } else {
            push(ROUTES.BASE_URL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const backToHome = () => {
        window.location.href = WORDPRESS_URL ?? "https://gomo.ie/";
    };

    const broadbandOffer = selectOfferInCartByServiceGroup(savedStorage?.cart, EServiceGroup.SA_BB);
    const mobileOffer = selectOfferInCartByServiceGroup(savedStorage?.cart, EServiceGroup.MOBILE);

    const getConfirmationBroadbandTitle = (offer: ICartOffer) => {
        if (offer.prequal?.isPreOrder) {
            return t("broadbandPreOrder");
        }
        if (offer.appointment?.type === EAppointmentType.NO_INSTALL) {
            return t("broadbandNoInstall");
        }

        return t("broadbandFullInstall");
    };

    const renderConfirmationBroadbandInfo = (offer: ICartOffer) => {
        if (offer.prequal?.isPreOrder && offer.prequal.serviceDate) {
            return <ConfirmationPreOrder serviceDate={offer.prequal.serviceDate} />;
        }

        if (offer.appointment?.type === EAppointmentType.NO_INSTALL && offer.activationAt) {
            return <ConfirmationNoInstall activationAt={offer.activationAt} />;
        }

        if (offer.appointment?.type === EAppointmentType.FULL_INSTALL) {
            return <ConfirmationFullInstall appointment={offer.appointment} />;
        }

        return undefined;
    };

    return (
        <div className={classes.container}>
            <Helmet>
                <meta content={t("title")} property="og:title" />
                <meta content="noindex, nofollow" name="robots" />
                <title>{t("title")}</title>
            </Helmet>
            <div className={classes.banner}>
                <div className={classes.bannerContentContainer}>
                    <div className={classes.bannerContent}>
                        <Typography className={classes.title} variant="h1">
                            {t("goodToGomo")}
                        </Typography>
                        <Typography className={classes.subtitle}>
                            {isTabletOrDesktop && (
                                <SvgIcon
                                    className={classes.checkIcon}
                                    color="primary"
                                    component={CheckIcon}
                                    inheritViewBox
                                />
                            )}
                            {t("orderConfirmation")}
                        </Typography>
                    </div>
                    <img alt="banner" className={classes.bannerImage} src={confirmationImage} />
                </div>
            </div>
            <div className={classes.confirmationContainer}>
                <Typography align="center" className={classes.orderNumber} paddingBlock={3}>
                    {t("orderNumber")}: #{savedStorage?.prospectReference}
                </Typography>
                <Grid container justifyContent="center" spacing={2}>
                    {broadbandOffer && (
                        <Grid item xs={12}>
                            <FlowCard title={getConfirmationBroadbandTitle(broadbandOffer)}>
                                {renderConfirmationBroadbandInfo(broadbandOffer)}
                            </FlowCard>
                        </Grid>
                    )}
                    {mobileOffer && (
                        <Grid item xs={12}>
                            <FlowCard title={t("simDelivery")}>
                                <ConfirmationMobile />
                            </FlowCard>
                        </Grid>
                    )}
                    <Grid className={classes.orderConfirmationEmail} item xs={12}>
                        <Typography align="center" color="secondary" variant="normal">
                            {t("orderConfirmationEmail")}
                        </Typography>
                    </Grid>
                    <Grid display="flex" item justifyContent="center" md={7} xs={12}>
                        <LabeledButton
                            className={classes.backHomeButton}
                            color="primary"
                            labelText={t("backHome")}
                            onClick={backToHome}
                        >
                            {t("backHome")}
                        </LabeledButton>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};
